import { memo } from 'react';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { getVisibleContexts } from '@/app/providers/contextRegistry';
import { useUser } from '@/app/providers/UserProvider';
import { cn } from '@/shared/lib/utils';
import { Button } from '@opal/components';
import type { AppContextConfig, AppContextType } from '@/app/providers/AppContext.types';

export const ContextSwitcherHorizontal = memo(function ContextSwitcherHorizontal() {
  const { isAdmin, isCurator } = useUser();
  const { activeContext, setActiveContext } = useActiveContext();
  const visibleContexts = getVisibleContexts(isAdmin, isCurator);

  return (
    <div className="px-3 pt-2 pb-2">
      <nav
        className="flex flex-row items-center p-1 bg-background-neutral-00 rounded-lg border border-border-01 overflow-x-auto"
        aria-label="Context switcher"
        data-testid="ContextSwitcherHorizontal"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
      {visibleContexts.map((ctx) => (
        <ContextTab
          key={ctx.id}
          config={ctx}
          isActive={activeContext === ctx.id}
          onSelect={setActiveContext}
        />
      ))}
      </nav>
    </div>
  );
});

interface ContextTabProps {
  config: AppContextConfig;
  isActive: boolean;
  onSelect: (ctx: AppContextType) => void;
}

const ContextTab = memo(function ContextTab({
  config,
  isActive,
  onSelect,
}: ContextTabProps) {
  const { id, icon: Icon, label, tooltip } = config;

  return (
    <button
      className={cn(
        "flex flex-row items-center justify-center gap-1.5 h-8 px-3 rounded-md transition-all duration-200 shrink-0 outline-none",
        isActive 
          ? "bg-background text-gray-900 dark:text-gray-50 shadow-sm font-bold border border-border-01" 
          : "text-text-03 hover:text-text-01 hover:bg-black/5 dark:hover:bg-white/5 border border-transparent font-medium"
      )}
      onClick={() => onSelect(id)}
      title={tooltip}
      aria-selected={isActive}
      data-context={id}
    >
      <Icon size={14} strokeWidth={isActive ? 2.5 : 2} aria-hidden="true" />
      <span className="text-xs whitespace-nowrap">{label}</span>
    </button>
  );
});
